package one.devsbox.www.ui.apps.wheel.edit

import androidx.compose.animation.Crossfade
import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.*
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import compose.assets.Svg
import compose.assets.svg.Plus
import compose.assets.svg.TrashOutline
import one.devsbox.shared.a.App
import one.devsbox.www.ui.widgets.*
import one.devsbox.www.www_portal.generated.resources.*
import org.jetbrains.compose.resources.stringResource
import tk.mallumo.compose.navigation.ComposableNavNode
import tk.mallumo.utils.DataState
import one.devsbox.www.tools.ArgsId
import one.devsbox.www.provider.LocalDisplayProvider
import androidx.compose.ui.graphics.drawscope.translate
import androidx.compose.foundation.Canvas
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.geometry.Size
import androidx.compose.ui.graphics.Path
import androidx.compose.ui.graphics.drawscope.Fill
import androidx.compose.ui.graphics.drawscope.rotate
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.drawText
import androidx.compose.ui.text.rememberTextMeasurer
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.runtime.remember
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.getValue
import androidx.compose.runtime.setValue
import androidx.compose.foundation.clickable
import kotlinx.browser.document
import org.w3c.dom.HTMLInputElement
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.ui.graphics.ImageBitmap
import androidx.compose.ui.unit.IntOffset
import androidx.compose.ui.unit.IntSize
import one.devsbox.www.io.Repository
import com.godaddy.android.colorpicker.ClassicColorPicker
import com.godaddy.android.colorpicker.HsvColor
import one.devsbox.www.tools.ArgsIdWithParent
import one.devsbox.www.tools.EFieldRegularLong

@Composable
@ComposableNavNode(ArgsIdWithParent::class)
fun AppWheelEUI() {
    with(AppWheelECtx.get()) {
        ContentAppWheelEUI()
    }
}

@Composable
context(ctx: AppWheelECtx)
private fun ContentAppWheelEUI() {
    Box(modifier = Modifier.fillMaxSize().background(MaterialTheme.colorScheme.background)) {
        Crossfade(targetState = ctx.vm.state) { state ->
            when (state) {
                is DataState.Idle -> Unit
                is DataState.Loading -> FullscreenLoadingUI()
                is DataState.Error -> {
                    FullscreenErrorUI(
                        message = state.message,
                        onBack = { ctx.action.back() },
                        onReload = { ctx.action.reload() }
                    )
                }

                is DataState.Result -> Box(modifier = Modifier.fillMaxSize()) {
                    SuccessUI(state.nnvl)
                    ContentActions(
                        contentActions = ctx.vm.contentActions,
                        colorInactive = ctx.colorSecondaryContainer,
                        colorActive = ctx.colorPrimaryContainer
                    )
                }
            }
        }

        val warnMsg = ctx.vm.warningMessage
        val warnRes = ctx.vm.warningRes
        if (warnMsg != null || warnRes != null) {
            AlertDialog(
                onDismissRequest = { ctx.action.clearWarning() },
                title = { Text(stringResource(Res.string.ui_devices_edit_deviceeui_error)) },
                text = { Text(if (warnRes != null) stringResource(warnRes) else warnMsg.orEmpty()) },
                confirmButton = {
                    TextButton(onClick = { ctx.action.clearWarning() }) {
                        Text(stringResource(Res.string.ui_devices_edit_deviceeui_ok))
                    }
                }
            )
        }
    }
}

@Composable
context(ctx: AppWheelECtx)
private fun SuccessUI(editor: AppWheelECtx.Editor) {
    val isNew = ctx.args.id <= 0
    val isMobile = LocalDisplayProvider.current.state.mobile

    val editScrollState = rememberScrollState(initial = ctx.vm.scrollPosition)
    LaunchedEffect(editScrollState.value) {
        ctx.vm.scrollPosition = editScrollState.value
    }

    Column(modifier = Modifier.fillMaxSize()) {
        Column(modifier = Modifier.padding(16.dp)) {
            Text(
                text = if (isNew) stringResource(Res.string.ui_app_instance_edit_new_wheel_game) else stringResource(
                    Res.string.ui_app_instance_edit_wheel_game_editor
                ),
                style = MaterialTheme.typography.headlineMedium,
                fontWeight = FontWeight.Bold,
                color = MaterialTheme.colorScheme.onSurface
            )
        }

        if (isMobile) {
            var activeTab by remember { mutableStateOf(1) } // Default selection of "EDIT" tab

            Box(modifier = Modifier.weight(1f)) {
                when (activeTab) {
                    0 -> {
                        Column(
                            modifier = Modifier
                                .fillMaxSize()
                                .padding(horizontal = 16.dp)
                                .verticalScroll(rememberScrollState())
                        ) {
                            PreviewGameSection(editor, modifier = Modifier.fillMaxWidth())
                            Spacer(modifier = Modifier.height(250.dp))
                        }
                    }

                    1 -> {
                        Column(
                            modifier = Modifier
                                .fillMaxSize()
                                .padding(horizontal = 16.dp)
                                .verticalScroll(editScrollState),
                            verticalArrangement = Arrangement.spacedBy(16.dp)
                        ) {
                            GeneralInfoSection(editor)
                            ThemeDesignSection(editor)
                            TimersQrSection(editor)
                            SectorsEditorSection(editor)
                            Spacer(modifier = Modifier.height(250.dp))
                        }
                    }
                }
            }

            TabRow(
                selectedTabIndex = activeTab,
                containerColor = MaterialTheme.colorScheme.surface,
                contentColor = MaterialTheme.colorScheme.primary
            ) {
                Tab(
                    selected = activeTab == 0,
                    onClick = { activeTab = 0 },
                    text = { Text(stringResource(Res.string.ui_app_instance_edit_tab_preview)) }
                )
                Tab(
                    selected = activeTab == 1,
                    onClick = { activeTab = 1 },
                    text = { Text(stringResource(Res.string.ui_locations_detail_btn_edit)) }
                )
            }
        } else {
            val isLand = editor.orientation.state == App.Wheel.Theme.ORIENTATION_LAND
            Row(
                modifier = Modifier
                    .fillMaxSize()
                    .padding(horizontal = 16.dp),
                horizontalArrangement = Arrangement.spacedBy(16.dp)
            ) {
                Box(
                    modifier = Modifier
                        .weight(if (isLand) 1f else 1f)
                        .fillMaxHeight(),
                    contentAlignment = Alignment.Center
                ) {
                    PreviewGameSection(editor, modifier = Modifier.fillMaxSize())
                }

                Column(
                    modifier = Modifier
                        .weight(if (isLand) 1f else 1.2f)
                        .fillMaxHeight()
                        .verticalScroll(editScrollState),
                    verticalArrangement = Arrangement.spacedBy(16.dp)
                ) {
                    GeneralInfoSection(editor)
                    ThemeDesignSection(editor)
                    TimersQrSection(editor)
                    SectorsEditorSection(editor)
                    Spacer(modifier = Modifier.height(250.dp))
                }
            }
        }
    }
}

@Composable
private fun PreviewGameSection(editor: AppWheelECtx.Editor, modifier: Modifier = Modifier) {
    val items = editor.items.state
    val textMeasurer = rememberTextMeasurer()
    val isLand = editor.orientation.state == App.Wheel.Theme.ORIENTATION_LAND
    val themeBgColor = editor.themeBgColor.state
    val backgroundApp = editor.themeBgApp.state
    val wheelScaleMultiplier = editor.wheelScale.state
    val wheelPosX = editor.wheelX.state
    val wheelPosY = editor.wheelY.state
    val itemFontSize = editor.themeItemFontSize.state
    val itemNameY = editor.themeItemNameY.state
    val themeItemIconsScale = editor.themeItemIconsScale.state
    val themeItemIconsY = editor.themeItemIconsY.state
    val wheelColor = editor.wheelColor.state
    val wheelCenterColor = editor.wheelCenterColor.state
    val themeTriangleColor = editor.themeTriangleColor.state
    val wheelScaleCenter = editor.wheelScaleCenter.state

    val loadedIcons = items.associate { sector ->
        sector.data.idGroup to rememberSectorIcon(sector.iconWebp.state)
    }

    Card(
        modifier = modifier,
        colors = CardDefaults.cardColors(containerColor = Color.Transparent),
        border = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant)
    ) {
        Column(modifier = Modifier.padding(16.dp).fillMaxHeight()) {
            LabelSection(text = stringResource(Res.string.ui_app_instance_detail_game_preview))
            Spacer(modifier = Modifier.height(16.dp))

            BoxWithConstraints(
                modifier = Modifier
                    .weight(1f)
                    .fillMaxWidth(),
                contentAlignment = Alignment.Center
            ) {
                val aspectRatio = if (isLand) 16f / 9f else 9f / 16f
                val widthPx = constraints.maxWidth
                val heightPx = constraints.maxHeight

                val boxModifier = if (widthPx.toFloat() / heightPx.toFloat() > aspectRatio) {
                    Modifier.fillMaxHeight().aspectRatio(aspectRatio)
                } else {
                    Modifier.fillMaxWidth().aspectRatio(aspectRatio)
                }

                Box(
                    modifier = boxModifier
                        .clip(RoundedCornerShape(8.dp))
                        .background(themeBgColor),
                    contentAlignment = Alignment.Center
                ) {
                    if (backgroundApp > 0 && backgroundApp != App.NO_IMAGE) {
                        LazyImage(
                            id = backgroundApp,
                            modifier = Modifier.fillMaxSize(),
                            contentScale = ContentScale.Crop,
                            alphaBackground = false
                        )
                    }

                    Canvas(
                        modifier = Modifier.fillMaxSize()
                    ) {
                        val wSize = size.minDimension * wheelScaleMultiplier
                        val wCenter = wSize / 2f
                        val xOffset = size.width * wheelPosX - wCenter
                        val yOffset = size.height * wheelPosY - wCenter
                        val itemRot = 360f / items.size.coerceAtLeast(1).toFloat()
                        val itemRotHalf = itemRot / 2f

                        translate(left = xOffset, top = yOffset) {
                            drawCircle(
                                color = wheelColor,
                                center = Offset(wCenter, wCenter),
                                radius = wCenter
                            )

                            items.sortedBy { it.position.state }.forEachIndexed { index, sector ->
                                rotate(itemRot * index.toFloat(), Offset(wCenter, wCenter)) {
                                    drawArc(
                                        color = sector.colorBg.state,
                                        startAngle = -90f - itemRotHalf,
                                        sweepAngle = itemRot,
                                        useCenter = true,
                                        size = Size(wSize - 4.dp.toPx(), wSize - 4.dp.toPx()),
                                        topLeft = Offset(2.dp.toPx(), 2.dp.toPx())
                                    )
                                }
                            }

                            items.forEachIndexed { index, sector ->
                                rotate(itemRot * index.toFloat(), Offset(wCenter, wCenter)) {
                                    val nameText = sector.name.text.text
                                    if (nameText.trim().isNotEmpty()) {
                                        val textStyle = TextStyle(
                                            fontSize = (wSize * 0.045f * itemFontSize).sp,
                                            color = sector.colorFg.state,
                                            fontWeight = FontWeight.Bold,
                                            textAlign = TextAlign.Center
                                        )
                                        val measured = textMeasurer.measure(
                                            text = nameText,
                                            style = textStyle
                                        )
                                        drawText(
                                            textLayoutResult = measured,
                                            topLeft = Offset(
                                                x = wCenter - (measured.size.width / 2f),
                                                y = wCenter * itemNameY
                                            )
                                        )
                                    }
                                }
                            }

                            items.forEachIndexed { index, sector ->
                                rotate(itemRot * index.toFloat(), Offset(wCenter, wCenter)) {
                                    val iconBitmap = loadedIcons[sector.data.idGroup]
                                    if (iconBitmap != null) {
                                        val iconSize = wSize * 0.12f * themeItemIconsScale
                                        val iconLeft = wCenter - (iconSize / 2f)
                                        val iconTop = wCenter * themeItemIconsY - (iconSize / 2f)
                                        drawImage(
                                            image = iconBitmap,
                                            dstOffset = IntOffset(
                                                iconLeft.toInt(),
                                                iconTop.toInt()
                                            ),
                                            dstSize = IntSize(iconSize.toInt(), iconSize.toInt())
                                        )
                                    }
                                }
                            }

                            repeat(items.size) { index ->
                                rotate(
                                    itemRot * index.toFloat() - itemRotHalf,
                                    Offset(wCenter, wCenter)
                                ) {
                                    drawLine(
                                        color = wheelColor,
                                        start = Offset(wCenter, wCenter),
                                        end = Offset(wCenter, 0f),
                                        strokeWidth = 2.dp.toPx()
                                    )
                                }
                            }

                            val brandingRadius = (wSize * wheelScaleCenter) / 2f
                            drawCircle(
                                color = wheelCenterColor,
                                center = Offset(wCenter, wCenter),
                                radius = brandingRadius
                            )

                            val pinPath = Path().apply {
                                moveTo(wCenter, 0f)
                                lineTo(wCenter - 10.dp.toPx(), -15.dp.toPx())
                                lineTo(wCenter + 10.dp.toPx(), -15.dp.toPx())
                                close()
                            }
                            drawPath(
                                path = pinPath,
                                color = themeTriangleColor,
                                style = Fill
                            )
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun GeneralInfoSection(editor: AppWheelECtx.Editor) {
    Card(
        modifier = Modifier.fillMaxWidth(),
        colors = CardDefaults.cardColors(containerColor = Color.Transparent),
        border = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant)
    ) {
        Column(
            modifier = Modifier.padding(16.dp),
            verticalArrangement = Arrangement.spacedBy(12.dp)
        ) {
            LabelSection(text = stringResource(Res.string.ui_app_instance_edit_general_config))

            Column(verticalArrangement = Arrangement.spacedBy(4.dp)) {
                LabelField(text = stringResource(Res.string.ui_app_instance_edit_app_name))
                OutlinedTextField(
                    value = editor.name.text,
                    onValueChange = { editor.name.set(it) },
                    modifier = Modifier.fillMaxWidth(),
                    shape = RoundedCornerShape(4.dp),
                    singleLine = true,
                    colors = OutlinedTextFieldDefaults.colors(
                        focusedBorderColor = MaterialTheme.colorScheme.primary,
                        unfocusedBorderColor = MaterialTheme.colorScheme.outlineVariant
                    )
                )
            }

            Column(verticalArrangement = Arrangement.spacedBy(4.dp)) {
                LabelField(text = stringResource(Res.string.ui_app_instance_edit_screen_orientation))
                Row(horizontalArrangement = Arrangement.spacedBy(16.dp)) {
                    val isPort = editor.orientation.state == App.Wheel.Theme.ORIENTATION_PORT
                    OutlinedButton(
                        onClick = { editor.orientation.set(App.Wheel.Theme.ORIENTATION_PORT) },
                        colors = ButtonDefaults.outlinedButtonColors(
                            containerColor = if (isPort) MaterialTheme.colorScheme.primaryContainer.copy(
                                alpha = 0.3f
                            ) else Color.Transparent,
                            contentColor = MaterialTheme.colorScheme.onSurface
                        ),
                        border = BorderStroke(
                            1.dp,
                            if (isPort) MaterialTheme.colorScheme.primary else MaterialTheme.colorScheme.outlineVariant
                        ),
                        modifier = Modifier.weight(1f),
                        shape = RoundedCornerShape(4.dp)
                    ) {
                        Text(
                            stringResource(Res.string.ui_app_instance_edit_portrait),
                            fontWeight = FontWeight.Bold
                        )
                    }
                    OutlinedButton(
                        onClick = { editor.orientation.set(App.Wheel.Theme.ORIENTATION_LAND) },
                        colors = ButtonDefaults.outlinedButtonColors(
                            containerColor = if (!isPort) MaterialTheme.colorScheme.primaryContainer.copy(
                                alpha = 0.3f
                            ) else Color.Transparent,
                            contentColor = MaterialTheme.colorScheme.onSurface
                        ),
                        border = BorderStroke(
                            1.dp,
                            if (!isPort) MaterialTheme.colorScheme.primary else MaterialTheme.colorScheme.outlineVariant
                        ),
                        modifier = Modifier.weight(1f),
                        shape = RoundedCornerShape(4.dp)
                    ) {
                        Text(
                            stringResource(Res.string.ui_app_instance_edit_landscape),
                            fontWeight = FontWeight.Bold
                        )
                    }
                }
            }

            Column(verticalArrangement = Arrangement.spacedBy(4.dp)) {
                LabelField(text = stringResource(Res.string.ui_app_instance_edit_wheel_scale))
                Row(
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.spacedBy(16.dp)
                ) {
                    Slider(
                        value = editor.wheelScale.state,
                        onValueChange = { editor.wheelScale.set(it) },
                        modifier = Modifier.weight(1f)
                    )
                    Text(
                        text = "${(editor.wheelScale.state * 100).toInt()}%",
                        fontWeight = FontWeight.Bold,
                        style = MaterialTheme.typography.bodyMedium
                    )
                }
            }

            Column(verticalArrangement = Arrangement.spacedBy(4.dp)) {
                LabelField(text = stringResource(Res.string.ui_app_instance_edit_wheel_position_x))
                Row(
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.spacedBy(16.dp)
                ) {
                    Slider(
                        value = editor.wheelX.state,
                        onValueChange = { editor.wheelX.set(it) },
                        modifier = Modifier.weight(1f)
                    )
                    Text(
                        text = "${(editor.wheelX.state * 100).toInt()}%",
                        fontWeight = FontWeight.Bold,
                        style = MaterialTheme.typography.bodyMedium
                    )
                }
            }

            Column(verticalArrangement = Arrangement.spacedBy(4.dp)) {
                LabelField(text = stringResource(Res.string.ui_app_instance_edit_wheel_position_y))
                Row(
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.spacedBy(16.dp)
                ) {
                    Slider(
                        value = editor.wheelY.state,
                        onValueChange = { editor.wheelY.set(it) },
                        modifier = Modifier.weight(1f)
                    )
                    Text(
                        text = "${(editor.wheelY.state * 100).toInt()}%",
                        fontWeight = FontWeight.Bold,
                        style = MaterialTheme.typography.bodyMedium
                    )
                }
            }

            Column(verticalArrangement = Arrangement.spacedBy(4.dp)) {
                LabelField(text = stringResource(Res.string.ui_app_instance_edit_wheel_center_scale))
                Row(
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.spacedBy(16.dp)
                ) {
                    Slider(
                        value = editor.wheelScaleCenter.state,
                        onValueChange = { editor.wheelScaleCenter.set(it) },
                        modifier = Modifier.weight(1f)
                    )
                    Text(
                        text = "${(editor.wheelScaleCenter.state * 100).toInt()}%",
                        fontWeight = FontWeight.Bold,
                        style = MaterialTheme.typography.bodyMedium
                    )
                }
            }
        }
    }
}

@Composable
context(ctx: AppWheelECtx)
private fun ThemeDesignSection(editor: AppWheelECtx.Editor) {
    Card(
        modifier = Modifier.fillMaxWidth(),
        colors = CardDefaults.cardColors(containerColor = Color.Transparent),
        border = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant)
    ) {
        Column(
            modifier = Modifier.padding(16.dp),
            verticalArrangement = Arrangement.spacedBy(12.dp)
        ) {
            LabelSection(text = stringResource(Res.string.ui_app_instance_edit_theme_design))

            ColorFieldPickerPopup(
                label = stringResource(Res.string.ui_app_instance_edit_bg_color),
                value = editor.themeBgColor.text,
                color = editor.themeBgColor.state,
                onValueChange = { editor.themeBgColor.setText(it) }
            )

            ColorFieldPickerPopup(
                label = stringResource(Res.string.ui_app_instance_edit_wheel_color),
                value = editor.wheelColor.text,
                color = editor.wheelColor.state,
                onValueChange = { editor.wheelColor.setText(it) }
            )

            ColorFieldPickerPopup(
                label = stringResource(Res.string.ui_app_instance_edit_wheel_center_color),
                value = editor.wheelCenterColor.text,
                color = editor.wheelCenterColor.state,
                onValueChange = { editor.wheelCenterColor.setText(it) }
            )

            ColorFieldPickerPopup(
                label = stringResource(Res.string.ui_app_instance_edit_marker_color),
                value = editor.themeTriangleColor.text,
                color = editor.themeTriangleColor.state,
                onValueChange = { editor.themeTriangleColor.setText(it) }
            )

            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.spacedBy(16.dp)
            ) {
                Box(Modifier.weight(1F)) {
                    ImageSelectorField(
                        label = stringResource(Res.string.ui_app_instance_edit_splash_image),
                        image = editor.themeBgStartup,
                        onPick = { ctx.action.pickSplash() },
                        modifier = Modifier.align(Alignment.Center)
                    )
                }
                Box(Modifier.weight(1F)) {
                    ImageSelectorField(
                        label = stringResource(Res.string.ui_app_instance_edit_game_bg_image),
                        image = editor.themeBgApp,
                        onPick = { ctx.action.pickBackgroundApp() },
                        modifier = Modifier.align(Alignment.Center)
                    )
                }

            }

            Column(verticalArrangement = Arrangement.spacedBy(4.dp)) {
                LabelField(text = stringResource(Res.string.ui_app_instance_edit_items_text_size))
                Row(
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.spacedBy(16.dp)
                ) {
                    Slider(
                        value = editor.themeItemFontSize.state,
                        onValueChange = { editor.themeItemFontSize.set(it) },
                        modifier = Modifier.weight(1f)
                    )
                    Text(
                        text = "${(editor.themeItemFontSize.state * 100).toInt()}%",
                        fontWeight = FontWeight.Bold,
                        style = MaterialTheme.typography.bodyMedium
                    )
                }
            }

            Column(verticalArrangement = Arrangement.spacedBy(4.dp)) {
                LabelField(text = stringResource(Res.string.ui_app_instance_edit_items_icon_size))
                Row(
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.spacedBy(16.dp)
                ) {
                    Slider(
                        value = editor.themeItemIconsScale.state,
                        onValueChange = { editor.themeItemIconsScale.set(it) },
                        modifier = Modifier.weight(1f)
                    )
                    Text(
                        text = "${(editor.themeItemIconsScale.state * 100).toInt()}%",
                        fontWeight = FontWeight.Bold,
                        style = MaterialTheme.typography.bodyMedium
                    )
                }
            }

            Column(verticalArrangement = Arrangement.spacedBy(4.dp)) {
                LabelField(text = stringResource(Res.string.ui_app_instance_edit_items_text_y))
                Row(
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.spacedBy(16.dp)
                ) {
                    Slider(
                        value = editor.themeItemNameY.state,
                        onValueChange = { editor.themeItemNameY.set(it) },
                        modifier = Modifier.weight(1f)
                    )
                    Text(
                        text = "${(editor.themeItemNameY.state * 100).toInt()}%",
                        fontWeight = FontWeight.Bold,
                        style = MaterialTheme.typography.bodyMedium
                    )
                }
            }

            Column(verticalArrangement = Arrangement.spacedBy(4.dp)) {
                LabelField(text = stringResource(Res.string.ui_app_instance_edit_items_icon_y))
                Row(
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.spacedBy(16.dp)
                ) {
                    Slider(
                        value = editor.themeItemIconsY.state,
                        onValueChange = { editor.themeItemIconsY.set(it) },
                        modifier = Modifier.weight(1f)
                    )
                    Text(
                        text = "${(editor.themeItemIconsY.state * 100).toInt()}%",
                        fontWeight = FontWeight.Bold,
                        style = MaterialTheme.typography.bodyMedium
                    )
                }
            }
        }
    }
}

@Composable
private fun ImageSelectorField(
    label: String,
    image: EFieldRegularLong,
    onPick: () -> Unit,
    modifier: Modifier = Modifier
) {
    Column(
        modifier = modifier,
        verticalArrangement = Arrangement.spacedBy(8.dp)
    ) {
        LabelField(text = label, modifier = Modifier.align(Alignment.CenterHorizontally))
        Box(
            modifier = Modifier
                .size(192.dp)
                .clip(RoundedCornerShape(4.dp))
                .background(MaterialTheme.colorScheme.surfaceVariant)
                .border(1.dp, MaterialTheme.colorScheme.outlineVariant, RoundedCornerShape(4.dp)),
            contentAlignment = Alignment.Center
        ) {
            if (image.state > 0 && image.state != App.NO_IMAGE) {
                LazyImage(
                    id = image.state,
                    modifier = Modifier.fillMaxSize()
                        .clickable { image.set(App.NO_IMAGE) },
                    contentScale = ContentScale.Fit,
                    alphaBackground = true
                )
            } else {
                Text(
                    text = stringResource(Res.string.ui_app_instance_edit_no_image),
                    style = MaterialTheme.typography.bodySmall,
                    color = MaterialTheme.colorScheme.onSurfaceVariant.copy(alpha = 0.6f)
                )
            }
        }

        OutlinedButton(
            onClick = onPick,
            modifier = Modifier.align(Alignment.CenterHorizontally),
            shape = RoundedCornerShape(4.dp),
            border = BorderStroke(1.dp, MaterialTheme.colorScheme.primary),
            colors = ButtonDefaults.outlinedButtonColors(
                contentColor = MaterialTheme.colorScheme.primary
            )
        ) {
            Text(
                text = if (image.state > 0 && image.state != App.NO_IMAGE) stringResource(Res.string.ui_app_instance_edit_change_image) else stringResource(
                    Res.string.ui_app_instance_edit_select_image
                ),
                fontWeight = FontWeight.Bold
            )
        }
    }
}

@Composable
private fun CompactImagePicker(
    label: String,
    image: EFieldRegularLong,
    onPick: () -> Unit
) {
    Column(verticalArrangement = Arrangement.spacedBy(4.dp)) {
        LabelField(text = label)
        Row(
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(8.dp)
        ) {
            Box(
                modifier = Modifier
                    .size(48.dp)
                    .clip(RoundedCornerShape(4.dp))
                    .background(MaterialTheme.colorScheme.surfaceVariant)
                    .border(
                        1.dp,
                        MaterialTheme.colorScheme.outlineVariant,
                        RoundedCornerShape(4.dp)
                    ),
                contentAlignment = Alignment.Center
            ) {
                if (image.state > 0 && image.state != App.NO_IMAGE) {
                    LazyImage(
                        id = image.state,
                        modifier = Modifier.fillMaxSize()
                            .clickable {
                                image.set(App.NO_IMAGE)
                            },
                        contentScale = ContentScale.Crop,
                        alphaBackground = true
                    )
                } else {
                    Text(
                        text = stringResource(Res.string.ui_app_instance_edit_none),
                        style = MaterialTheme.typography.labelSmall,
                        color = MaterialTheme.colorScheme.onSurfaceVariant.copy(alpha = 0.6f)
                    )
                }
            }

            OutlinedButton(
                onClick = onPick,
                shape = RoundedCornerShape(4.dp),
                border = BorderStroke(1.dp, MaterialTheme.colorScheme.primary),
                contentPadding = PaddingValues(horizontal = 8.dp, vertical = 4.dp),
                modifier = Modifier.height(36.dp),
                colors = ButtonDefaults.outlinedButtonColors(
                    contentColor = MaterialTheme.colorScheme.primary
                )
            ) {
                Text(
                    stringResource(Res.string.ui_app_instance_edit_btn_pick),
                    fontWeight = FontWeight.Bold,
                    style = MaterialTheme.typography.labelMedium
                )
            }
        }
    }
}

@Composable
private fun TimersQrSection(editor: AppWheelECtx.Editor) {
    Card(
        modifier = Modifier.fillMaxWidth(),
        colors = CardDefaults.cardColors(containerColor = Color.Transparent),
        border = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant)
    ) {
        Column(
            modifier = Modifier.padding(16.dp),
            verticalArrangement = Arrangement.spacedBy(12.dp)
        ) {
            LabelSection(text = stringResource(Res.string.ui_app_instance_edit_timers_config))

            Row(horizontalArrangement = Arrangement.spacedBy(16.dp)) {
                Column(
                    modifier = Modifier.weight(1f),
                    verticalArrangement = Arrangement.spacedBy(4.dp)
                ) {
                    LabelField(text = stringResource(Res.string.ui_app_instance_edit_splash_timeout))
                    OutlinedTextField(
                        value = editor.timerSplash.text,
                        onValueChange = { editor.timerSplash.set(it) },
                        modifier = Modifier.fillMaxWidth(),
                        shape = RoundedCornerShape(4.dp),
                        singleLine = true,
                        colors = OutlinedTextFieldDefaults.colors(
                            focusedBorderColor = MaterialTheme.colorScheme.primary,
                            unfocusedBorderColor = MaterialTheme.colorScheme.outlineVariant
                        )
                    )
                }

                Column(
                    modifier = Modifier.weight(1f),
                    verticalArrangement = Arrangement.spacedBy(4.dp)
                ) {
                    LabelField(text = stringResource(Res.string.ui_app_instance_edit_game_timeout))
                    OutlinedTextField(
                        value = editor.timerGame.text,
                        onValueChange = { editor.timerGame.set(it) },
                        modifier = Modifier.fillMaxWidth(),
                        shape = RoundedCornerShape(4.dp),
                        singleLine = true,
                        colors = OutlinedTextFieldDefaults.colors(
                            focusedBorderColor = MaterialTheme.colorScheme.primary,
                            unfocusedBorderColor = MaterialTheme.colorScheme.outlineVariant
                        )
                    )
                }

                Column(
                    modifier = Modifier.weight(1f),
                    verticalArrangement = Arrangement.spacedBy(4.dp)
                ) {
                    LabelField(text = stringResource(Res.string.ui_app_instance_edit_result_timeout))
                    OutlinedTextField(
                        value = editor.timerResult.text,
                        onValueChange = { editor.timerResult.set(it) },
                        modifier = Modifier.fillMaxWidth(),
                        shape = RoundedCornerShape(4.dp),
                        singleLine = true,
                        colors = OutlinedTextFieldDefaults.colors(
                            focusedBorderColor = MaterialTheme.colorScheme.primary,
                            unfocusedBorderColor = MaterialTheme.colorScheme.outlineVariant
                        )
                    )
                }
            }
        }
    }
}

@Composable
context(ctx: AppWheelECtx)
private fun SectorsEditorSection(editor: AppWheelECtx.Editor) {
    Card(
        modifier = Modifier.fillMaxWidth(),
        colors = CardDefaults.cardColors(containerColor = Color.Transparent),
        border = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant)
    ) {
        Column(modifier = Modifier.padding(16.dp)) {
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically
            ) {
                LabelSection(text = stringResource(Res.string.ui_app_instance_edit_wheel_items))
                IconButton(onClick = { ctx.action.addSector() }) {
                    Icon(
                        imageVector = Svg.Plus,
                        contentDescription = stringResource(Res.string.ui_app_instance_edit_add_item_desc),
                        tint = MaterialTheme.colorScheme.primary
                    )
                }
            }

            Spacer(modifier = Modifier.height(16.dp))

            Column(verticalArrangement = Arrangement.spacedBy(12.dp)) {
                editor.items.state.forEach { sector ->
                    SectorItemRow(sector) {
                        ctx.action.deleteSector(sector)
                    }
                }
            }
        }
    }
}

@Composable
context(ctx: AppWheelECtx)
private fun SectorItemRow(
    sector: AppWheelECtx.Editor.ItemEditor,
    onDelete: () -> Unit
) {
    Card(
        modifier = Modifier.fillMaxWidth(),
        colors = CardDefaults.cardColors(containerColor = Color.Transparent),
        border = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant.copy(alpha = 0.5f))
    ) {
        Column(
            modifier = Modifier.padding(12.dp),
            verticalArrangement = Arrangement.spacedBy(8.dp)
        ) {
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.Bottom
            ) {
                Column(modifier = Modifier.weight(1f)) {
                    LabelField(stringResource(Res.string.ui_app_instance_edit_label))
                    OutlinedTextField(
                        value = sector.name.text,
                        onValueChange = { sector.name.set(it) },
                        modifier = Modifier.fillMaxWidth(),
                        shape = RoundedCornerShape(4.dp),
                        singleLine = false,
                        colors = OutlinedTextFieldDefaults.colors(
                            focusedBorderColor = MaterialTheme.colorScheme.primary,
                            unfocusedBorderColor = MaterialTheme.colorScheme.outlineVariant
                        )
                    )
                }

                Spacer(modifier = Modifier.width(12.dp))

                Column(modifier = Modifier.width(100.dp)) {
                    LabelField(stringResource(Res.string.ui_app_instance_edit_rewards_count))
                    OutlinedTextField(
                        value = sector.count.text,
                        onValueChange = { sector.count.set(it) },
                        modifier = Modifier.fillMaxWidth(),
                        shape = RoundedCornerShape(4.dp),
                        singleLine = true,
                        colors = OutlinedTextFieldDefaults.colors(
                            focusedBorderColor = MaterialTheme.colorScheme.primary,
                            unfocusedBorderColor = MaterialTheme.colorScheme.outlineVariant
                        )
                    )
                }

                Spacer(modifier = Modifier.width(8.dp))

                IconButton(
                    onClick = onDelete,
                    modifier = Modifier.padding(bottom = 4.dp)
                ) {
                    Icon(
                        imageVector = Svg.TrashOutline,
                        contentDescription = stringResource(Res.string.ui_app_instance_edit_delete_desc),
                        tint = MaterialTheme.colorScheme.error
                    )
                }
            }

            Row(horizontalArrangement = Arrangement.spacedBy(16.dp)) {
                Box(modifier = Modifier.weight(1f)) {
                    ColorFieldPickerPopup(
                        label = stringResource(Res.string.ui_app_instance_edit_background),
                        value = sector.colorBg.text,
                        color = sector.colorBg.state,
                        onValueChange = { sector.colorBg.setText(it) }
                    )
                }
                Box(modifier = Modifier.weight(1f)) {
                    ColorFieldPickerPopup(
                        label = stringResource(Res.string.ui_app_instance_edit_text_color),
                        value = sector.colorFg.text,
                        color = sector.colorFg.state,
                        onValueChange = { sector.colorFg.setText(it) }
                    )
                }
            }

            Row(horizontalArrangement = Arrangement.spacedBy(16.dp)) {
                Box(modifier = Modifier.weight(1f)) {
                    CompactImagePicker(
                        label = stringResource(Res.string.ui_app_instance_edit_icon),
                        image = sector.iconWebp,
                        onPick = { ctx.action.pickSectorIcon(sector) }
                    )
                }
                Box(modifier = Modifier.weight(1f)) {
                    CompactImagePicker(
                        label = stringResource(Res.string.ui_app_instance_edit_reward_visual),
                        image = sector.rewardVisual,
                        onPick = { ctx.action.pickSectorReward(sector) }
                    )
                }
            }
        }
    }
}


@Composable
fun rememberSectorIcon(id: Long): ImageBitmap? {
    if (id <= 0 || id == App.NO_IMAGE) return null
    var bitmap by remember(id) { mutableStateOf(Repository.getCached(id)) }
    if (bitmap == null) {
        LaunchedEffect(id) {
            runCatching {
                val fetched = Repository.fetchThumb(id)
                bitmap = fetched
            }
        }
    }
    return bitmap
}
